import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import HeaderInfo from "../components/common/HeaderInfo";
import DataTable from "../components/common/DataTable";
import CategoryModal from "../components/modals/CategoryModal";
import DeleteModal from "../components/modals/DeleteModal";
import { Edit } from "../components/icons";
import {
  useCreateCategoryMutation,
  useGetCategoriesQuery,
  useUpdateCategoryMutation,
} from "../store/api";

export default function Categories() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading, isFetching, error } = useGetCategoriesQuery({
    page,
    search: debouncedSearch,
  });
  const [createCategory, { isLoading: isCreating }] =
    useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] =
    useUpdateCategoryMutation();

  useEffect(() => {
    if (error) {
      toast.error(error?.data?.message || "Failed to load categories");
    }
  }, [error]);

  const categories = data?.data?.categories || [];
  
  const backendTotalPages = data?.data?.pagination?.totalPages || data?.pagination?.totalPages || data?.data?.totalPages;
  let totalPages = backendTotalPages || 1;
  let displayCategories = categories;

  // Fallback to frontend pagination ONLY if backend doesn't paginate at all
  if (!backendTotalPages && categories.length > 0) {
    totalPages = Math.ceil(categories.length / 10) || 1;
    displayCategories = categories.slice((page - 1) * 10, page * 10);
  }

  function handleCloseEdit() {
    setEditOpen(false);
    setEditRow(null);
  }

  function handleOpenEdit(row) {
    setEditRow(row);
    setEditOpen(true);
  }

  function handleCloseDelete() {
    setDeleteOpen(false);
    setDeleteRow(null);
  }

  // function handleOpenDelete(row) {
  //   setDeleteRow(row);
  //   setDeleteOpen(true);
  // }

  function handleNext() {
    if (page < totalPages) setPage((p) => p + 1);
  }

  function handlePrev() {
    if (page > 1) setPage((p) => p - 1);
  }

  const rows = displayCategories;

  const renderRow = (r, idx) => (
    <div key={r._id} className="bg-[#EEEEEE40]">
      <div className="grid grid-cols-12 gap-4 px-7 py-6 items-center border-b border-[#E5E5E5] ">
        <div className="col-span-4 flex items-center gap-[14px] text-black">
          <div className="w-[53px] h-[42px] flex justify-center items-center rounded bg-[#E4E3E5] capitalize">
            <p>{r.name.charAt(0)}</p>
          </div>
          <div className="capitalize">{r.name}</div>
        </div>

        <div className="col-span-3 text-sm text-[#878686]">
          {new Date(r.createdAt).toLocaleDateString("en-US", {
            timeZone: "Asia/Karachi",
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })}
        </div>

        <div className="col-span-3 text-sm text-[#878686]">
          {new Date(r.updatedAt).toLocaleDateString("en-US", {
            timeZone: "Asia/Karachi",
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })}
        </div>

        <div className="col-span-2 flex justify-end gap-3">
          <button aria-label="edit" onClick={() => handleOpenEdit({ id: r._id, name: r.name })}>
            <Edit />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pt-10 lg:pt-14 px-6 xl:pl-8 xl:pr-12 min-h-screen">
      <HeaderInfo
        title="Categories"
        desc="Manage your inventory item categories"
        btnLabel="Add Category"
        onAdd={() => setAddOpen(true)}
        isSearchBar={true}
        searchPlaceholder="Search Categories"
        searchValue={search}
        onSearchChange={setSearch}
        showFilterButton={false}
      />

      <div className="mt-4 lg:mt-6 lg:ml-2.5">
        <DataTable
          columns={[
            { key: "name", label: "Category Name", className: "col-span-4" },
            { key: "createdAt", label: "Created At", className: "col-span-3" },
            { key: "updatedAt", label: "Updated At", className: "col-span-3" },
            {
              key: "actions",
              label: "Actions",
              className: "col-span-2 text-right",
            },
          ]}
          rows={rows}
          renderRow={renderRow}
          page={page}
          totalPages={totalPages}
          onNext={handleNext}
          onPrev={handlePrev}
          isLoading={isLoading || isFetching}
        />
      </div>

      <CategoryModal
        open={addOpen}
        mode="add"
        isLoading={isCreating}
        onClose={() => setAddOpen(false)}
        onSubmit={async (name) => {
          try {
            const response = await createCategory({ name }).unwrap();
            if (response?.success === false) {
              toast.error(response?.message || "Failed to create category");
              return;
            }
            toast.success("Category created successfully");
            setAddOpen(false);
          } catch (error) {
            console.error(error);
            toast.error(error?.data?.message || "Failed to create category");
          }
        }}
      />

      <CategoryModal
        open={editOpen}
        mode="edit"
        isLoading={isUpdating}
        onClose={handleCloseEdit}
        initialValue={editRow?.name || ""}
        onSubmit={async (name) => {
          try {
            const response = await updateCategory({
              id: editRow.id,
              name,
            }).unwrap();
            if (response?.success === false) {
              toast.error(response?.message || "Failed to update category");
              return;
            }
            toast.success("Category updated successfully");
            handleCloseEdit();
          } catch (error) {
            console.error(error);
            toast.error(error?.data?.message || "Failed to update category");
          }
        }}
      />

      <DeleteModal
        open={deleteOpen}
        onClose={handleCloseDelete}
        onCreate={() => console.log("delete category", deleteRow)}
        modalName="Category"
      />
    </div>
  );
}
